import { PLATFORM_SKIES, PLATFORM_SOUNDSCAPES, PLATFORM_TEXTURES } from './assets.generated.js';
import { BLOCK_CAPS, WORLD_PACKAGE_BUDGET, WORLD_PACKAGE_LIMITS, WORLD_PACKAGE_PLACEMENT_BUDGET, packageExtras, } from './format.js';
import { propCost } from './props.js';
import { lookup } from './util.js';
/**
 * What a package costs a Quest 3 (W5), counted as apps/headset's render/worlds/budget.ts counts a
 * code world. The headset's package-facts.test.ts holds SKY_COST and WORLD_FIXED to its own numbers,
 * and Track A's block tests hold each block's built meshes to `blockCost`.
 */
export const SKY_COST = {
    /** look/sky-parts.ts SKY_LIGHT_WIDTH. */
    lightWidth: 1024,
    /** look/sun.ts SHADOW_MAP. */
    shadowMap: 1024,
    /** pano-maths PANO_FACE_MAX: the seen sky's cube face at most. */
    faceMax: 1440,
};
/** What every built world has whatever it holds: its halo field (one draw call, no halos). */
export const WORLD_FIXED = { triangles: 0, drawCalls: 1 };
/** A megabyte as the budget counts it (decimal); the Regelcheck hashes it (regelcheck.ts). */
export const MB = 1e6;
const mipmapped = (width, height) => Math.round(width * height * 4 * (4 / 3));
/** pano-maths cubeFace: a third of the width in multiples of 16, at most faceMax. */
export const skyFace = (width) => Math.min(SKY_COST.faceMax, Math.round(width / 3 / 16) * 16);
/** The sky's GPU memory: the cube when seen, the PMREM of its light, the shadow map (budget.ts). */
export function skyTextureBytes(width, seen) {
    const face = skyFace(width);
    const cube = seen ? face * face * 6 * 4 : 0;
    const lightWidth = Math.min(width, SKY_COST.lightWidth);
    const pmremCube = lightWidth / 4;
    const pmrem = 3 * Math.max(pmremCube, 112) * 4 * pmremCube * 8;
    const shadow = 2 * SKY_COST.shadowMap * SKY_COST.shadowMap * 4;
    return cube + pmrem + shadow;
}
/** A block's declared cost from its settings: the most it may build. */
export function blockCost(block) {
    switch (block.type) {
        case 'water': {
            const disc = { triangles: 128 * 90 * 2, drawCalls: 1 };
            if (!block.surf)
                return { ...disc, textureBytes: 0 };
            const n = block.surf.shore.length;
            const segments = block.surf.closed ? n : n - 1;
            return { triangles: disc.triangles + segments * 2, drawCalls: 2, textureBytes: 0 };
        }
        case 'grass':
            return {
                triangles: block.clumps * block.perClump[1] * 6,
                drawCalls: 1,
                textureBytes: mipmapped(128, 128),
            };
        case 'birds':
            return { triangles: block.flights.length * 6, drawCalls: 1, textureBytes: 0 };
        case 'deeltjes':
            if (block.kind === 'vlam')
                return { triangles: 48, drawCalls: 2, textureBytes: 0 };
            if (block.kind === 'zweven' || block.kind === 'vonken')
                return { triangles: 0, drawCalls: 1, textureBytes: mipmapped(32, 32) };
            return { triangles: block.count * 2, drawCalls: 1, textureBytes: mipmapped(64, 64) };
        case 'zwerm':
            return {
                triangles: block.count * (block.kind === 'vissen' ? 9 : 4),
                drawCalls: 1,
                textureBytes: 0,
            };
        case 'route':
            // The part's triangles are counted per copy in packageCost, once measured (V3).
            return { triangles: 0, drawCalls: 1, textureBytes: 0 };
        case 'surface':
        case 'draaien':
        case 'zichtbaar':
        case 'kleur':
        case 'licht':
        case 'plaatsruimte':
            return { triangles: 0, drawCalls: 0, textureBytes: 0 };
    }
}
/** The triangles of the measured meshes whose names start with `part`, in the package's own GLBs. */
export function routePartTriangles(pkg, measured, part) {
    let sum = 0;
    for (const file of pkg.terrain.glb) {
        const glb = lookup(measured.glbs, file);
        for (const mesh of glb?.meshes ?? [])
            if (mesh.name.startsWith(part))
                sum += mesh.triangles;
    }
    return sum;
}
/** How many measured meshes' names start with `part`, in the package's own GLBs. */
export function routePartMeshes(pkg, measured, part) {
    let count = 0;
    for (const file of pkg.terrain.glb)
        for (const mesh of lookup(measured.glbs, file)?.meshes ?? [])
            if (mesh.name.startsWith(part))
                count++;
    return count;
}
/** The whole package: blocks declared, GLBs and textures measured (or nothing yet), the platform's files. */
export function packageCost(pkg, measured) {
    const parts = [];
    let textureBytes = 0;
    let download = 0;
    for (const block of pkg.blocks) {
        const cost = blockCost(block);
        parts.push({
            name: `${block.type}:${block.id}`,
            triangles: cost.triangles,
            drawCalls: cost.drawCalls,
        });
        textureBytes += cost.textureBytes;
    }
    parts.push({ name: 'world', triangles: WORLD_FIXED.triangles, drawCalls: WORLD_FIXED.drawCalls });
    if (pkg.sky.seen)
        parts.push({ name: 'sky', triangles: 12, drawCalls: 1 });
    for (const file of pkg.terrain.glb) {
        if (!measured)
            continue;
        const glb = lookup(measured.glbs, file);
        if (!glb) {
            // Not measured is not free: counted as over the budget, and measuredProblems says why.
            parts.push({
                name: `glb:${file}`,
                triangles: WORLD_PACKAGE_BUDGET.triangles + 1,
                drawCalls: 1,
            });
            continue;
        }
        parts.push({ name: `glb:${file}`, triangles: glb.triangles, drawCalls: glb.drawCalls });
        for (const t of glb.textures)
            textureBytes += mipmapped(t.width, t.height);
    }
    // A route's copies: its moving part's measured triangles, once for every one of its objects.
    if (measured) {
        for (const block of pkg.blocks) {
            if (block.type !== 'route')
                continue;
            const part = routePartTriangles(pkg, measured, block.part);
            // Instanced per piece of the part: each of its meshes is a draw call (the block counted one).
            const meshes = routePartMeshes(pkg, measured, block.part);
            parts.push({
                name: `route-copies:${block.id}`,
                triangles: block.count * part,
                drawCalls: Math.max(0, meshes - 1),
            });
        }
    }
    // The platform's own models, placed: counted as budget.ts counts a code world's props; a model's
    // textures and bytes once, however often it is placed.
    const seenModels = new Set();
    for (const prop of packageExtras(pkg).props) {
        const cost = propCost(prop);
        parts.push({
            name: `prop:${prop.model} ×${prop.at.length}`,
            triangles: cost.triangles,
            drawCalls: cost.drawCalls,
        });
        if (seenModels.has(prop.model))
            continue;
        seenModels.add(prop.model);
        textureBytes += cost.textureBytes;
        download += cost.bytes;
    }
    // Textures named by blocks: the platform's once per map, the package's as measured.
    const platformMaps = new Set();
    for (const block of pkg.blocks) {
        if (block.type !== 'surface')
            continue;
        if ('platform' in block.texture) {
            for (const map of block.maps)
                platformMaps.add(`${block.texture.platform}/${map}`);
        }
        else {
            if (measured) {
                const size = lookup(measured.textures, block.texture.file);
                const side = WORLD_PACKAGE_LIMITS.textureSide;
                // Unmeasured: the most a texture may be.
                textureBytes += mipmapped(size?.width ?? side, size?.height ?? side);
            }
        }
        if (block.overlay)
            platformMaps.add(`${block.overlay.platform}/diff`);
    }
    for (const key of platformMaps) {
        const [asset, map] = key.split('/');
        const file = lookup(PLATFORM_TEXTURES, asset)?.[map];
        if (!file)
            continue;
        textureBytes += mipmapped(file.width, file.height);
        download += file.bytes;
    }
    const platformSky = 'platform' in pkg.sky.image ? lookup(PLATFORM_SKIES, pkg.sky.image.platform) : undefined;
    // A package's own sky is as measured; unmeasured, the widest allowed. (Before any measuring, a rough 1024.)
    const skyWidth = platformSky
        ? platformSky.width
        : measured
            ? (measured.sky?.width ?? WORLD_PACKAGE_LIMITS.skyWidth)
            : 1024;
    textureBytes += skyTextureBytes(skyWidth, pkg.sky.seen);
    download += platformSky?.bytes ?? 0;
    if ('soundscape' in pkg.sound)
        download += lookup(PLATFORM_SOUNDSCAPES, pkg.sound.soundscape)?.bytes ?? 0;
    // The processed files are downloaded, but not the picture: it is for briefs, not for the headset.
    const picture = packageExtras(pkg).picture;
    for (const [name, file] of Object.entries(measured?.files ?? {}))
        if (name !== picture)
            download += file.bytes;
    const triangles = parts.reduce((s, p) => s + p.triangles, 0);
    const drawCalls = parts.reduce((s, p) => s + p.drawCalls, 0);
    // A world with a plaatsruimte is held to the villa's line: the pupils' work comes on top (V3).
    const B = pkg.blocks.some((b) => b.type === 'plaatsruimte')
        ? WORLD_PACKAGE_PLACEMENT_BUDGET
        : WORLD_PACKAGE_BUDGET;
    const problems = [];
    if (triangles > B.triangles)
        problems.push({
            path: 'files',
            message: `te veel driehoeken: ${triangles} (hooguit ${B.triangles})`,
        });
    if (drawCalls > B.drawCalls)
        problems.push({
            path: 'files',
            message: `te veel tekenopdrachten: ${drawCalls} (hooguit ${B.drawCalls})`,
        });
    if (textureBytes / MB > B.textureMB)
        problems.push({
            path: 'files',
            message: `te veel textuurgeheugen: ${(textureBytes / MB).toFixed(1)} MB (hooguit ${B.textureMB})`,
        });
    if (download / MB > B.downloadMB)
        problems.push({
            path: 'files',
            message: `te groot om te downloaden: ${(download / MB).toFixed(1)} MB (hooguit ${B.downloadMB})`,
        });
    return {
        triangles,
        drawCalls,
        textureMB: textureBytes / MB,
        downloadMB: download / MB,
        parts,
        problems,
    };
}
/**
 * The caps over all blocks of a kind (V3): the headset clamps to the same numbers. A state may raise a
 * particle glow from 0, so a glow in any state counts as a halo.
 */
export function capProblems(pkg) {
    const out = [];
    const sum = (pick) => pkg.blocks.reduce((total, b) => total + pick(b), 0);
    const particles = (kinds) => (b) => b.type === 'deeltjes' && kinds.includes(b.kind) ? b.count : 0;
    const totals = [
        [sum(particles(['zweven'])), BLOCK_CAPS.drift, 'zweven samen hooguit {cap} deeltjes'],
        [
            sum(particles(['rook', 'aswolk', 'stoom'])),
            BLOCK_CAPS.puffs,
            'rook, aswolk en stoom samen hooguit {cap} wolkjes',
        ],
        [sum(particles(['vonken'])), BLOCK_CAPS.sparks, 'vonken samen hooguit {cap}'],
        [sum(particles(['vlam'])), BLOCK_CAPS.flames, 'hooguit {cap} vlammen'],
        [
            sum((b) => (b.type === 'zwerm' ? b.count : 0)),
            BLOCK_CAPS.swarm,
            'zwermen samen hooguit {cap} dieren',
        ],
        [
            sum((b) => (b.type === 'route' ? b.count : 0)),
            BLOCK_CAPS.route,
            'routes samen hooguit {cap} voertuigen',
        ],
        [
            sum((b) => (b.type === 'licht' ? b.lamps.filter((l) => l.intensity > 0).length : 0)),
            BLOCK_CAPS.lights,
            'samen hooguit {cap} lampen die echt licht geven',
        ],
        [
            sum((b) => b.type === 'licht'
                ? b.lamps.filter((l) => l.halo > 0).length
                : b.type === 'deeltjes'
                    ? // The ash cloud always has its two glows under it; others a halo when they glow
                        // (a state can only change a glow that is there: the checker refuses one from 0).
                        b.kind === 'aswolk'
                            ? 2
                            : b.glow > 0
                                ? 1
                                : 0
                    : 0),
            BLOCK_CAPS.halos,
            'samen hooguit {cap} gloeden',
        ],
    ];
    for (const [total, cap, text] of totals)
        if (total > cap)
            out.push({
                path: '/blocks',
                message: `${text.replace('{cap}', String(cap))}, niet ${total}`,
            });
    pkg.blocks.forEach((b, i) => {
        if (b.type === 'deeltjes' && b.kind === 'vlam' && b.count !== 1)
            out.push({ path: `/blocks/${i}/count`, message: 'een vlam is er één (count 1)' });
        if (b.type === 'route' && (b.present[1] > b.count || b.present[0] > b.present[1]))
            out.push({
                path: `/blocks/${i}/present`,
                message: 'moet [minst, meest] zijn met meest hooguit count',
            });
    });
    return out;
}
//# sourceMappingURL=cost.js.map